import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

interface UsePromptProps {
  isAuthenticated: boolean;
  isAuthInitialized: boolean;
  isPricingModalOpen: boolean;
}

export function usePrompt({
  isAuthenticated,
  isAuthInitialized,
  isPricingModalOpen,
}: UsePromptProps) {
  const [contentInput, setContentInput] = useState('');
  const contentEditableRef = useRef<HTMLTextAreaElement>(null);
  const searchParams = useSearchParams();

  // Sync ContentEditable
  useEffect(() => {
    if (contentEditableRef.current && contentEditableRef.current.value !== contentInput) {
      contentEditableRef.current.value = contentInput;
    }
  }, [contentInput]);

  // Save prompt when pricing modal opens
  useEffect(() => {
    if (isPricingModalOpen && contentInput.trim()) {
      localStorage.setItem('sora_prompt_backup', contentInput);
    }
  }, [isPricingModalOpen, contentInput]);

  // Restore prompt if payment was canceled
  useEffect(() => {
    const canceled = searchParams.get('canceled');
    if (canceled === 'true') {
      const backup = localStorage.getItem('sora_prompt_backup');
      if (backup) {
        setContentInput(backup);
        if (contentEditableRef.current) {
          contentEditableRef.current.value = backup;
        }
      }
    }
  }, [searchParams, setContentInput, contentEditableRef]);

  // Reset UI state on logout (Prompt portion)
  useEffect(() => {
    if (isAuthInitialized && !isAuthenticated) {
      setContentInput('');
      if (contentEditableRef.current) {
        contentEditableRef.current.value = '';
      }
    }
  }, [isAuthenticated, isAuthInitialized]);

  // Persist state across page refreshes
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (!hasRestoredRef.current) {
      const savedPrompt = localStorage.getItem('sora_generator_prompt');
      if (savedPrompt && !contentInput) {
        setContentInput(savedPrompt);
      }
      hasRestoredRef.current = true;
    }
  }, [contentInput, setContentInput]);

  useEffect(() => {
    if (contentInput.trim()) {
      localStorage.setItem('sora_generator_prompt', contentInput);
    } else {
      localStorage.removeItem('sora_generator_prompt');
    }
  }, [contentInput]);

  // Auto-resize textarea when contentInput changes
  useEffect(() => {
    const textarea = contentEditableRef.current;
    if (textarea) {
      textarea.style.height = 'auto'; // Reset height to recalculate
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [contentInput]);

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    setContentInput(e.currentTarget.value);
  };

  return {
    contentInput,
    setContentInput,
    contentEditableRef,
    handleInput,
  };
}
